import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { AlertTriangle, RotateCcw } from "lucide-react-native";
import { FocusablePressable } from "./FocusablePressable";
import { systemSansFont } from "../theme/tokens";

type Props = { children: React.ReactNode };
type State = { error: Error | null; resetKey: number };

export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, resetKey: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Jellyflix screen render failed", error, info.componentStack);
  }

  private retry = () => {
    this.setState((state) => ({ error: null, resetKey: state.resetKey + 1 }));
  };

  render() {
    if (!this.state.error) {
      return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
    }

    const detail = this.state.error.message.split("\n", 1)[0] || "An unexpected render error occurred.";
    return (
      <SafeAreaView style={styles.safe} edges={["top", "right", "bottom", "left"]}>
        <View accessibilityRole="alert" style={styles.card}>
          <View style={styles.headingRow}>
            <View style={styles.icon}><AlertTriangle color="#fca5a5" size={19} /></View>
            <View style={styles.copy}>
              <Text style={styles.title}>This screen hit a problem</Text>
              <Text numberOfLines={2} style={styles.detail}>{detail}</Text>
            </View>
          </View>
          <FocusablePressable
            accessibilityLabel="Try rendering this screen again"
            onPress={this.retry}
            style={(focused, pressed, hovered) => [
              styles.retry,
              (focused || hovered) && styles.retryActive,
              pressed && styles.retryPressed,
            ]}
          >
            <RotateCcw color="#111" size={15} />
            <Text style={styles.retryText}>Try Again</Text>
          </FocusablePressable>
        </View>
      </SafeAreaView>
    );
  }
}

const styles = StyleSheet.create({
  safe: { flex: 1, padding: 20, alignItems: "flex-end", justifyContent: "flex-start", backgroundColor: "#050505" },
  card: {
    alignSelf: "stretch",
    maxWidth: 400,
    padding: 16,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.14)",
    borderRadius: 9,
    gap: 14,
    backgroundColor: "rgba(16,16,16,0.97)",
    shadowColor: "#000",
    shadowOpacity: 0.5,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
    elevation: 16,
  },
  headingRow: { flexDirection: "row", alignItems: "flex-start", gap: 11 },
  icon: { width: 34, height: 34, borderRadius: 17, alignItems: "center", justifyContent: "center", backgroundColor: "rgba(229,9,20,0.16)" },
  copy: { minWidth: 0, flex: 1 },
  title: { color: "#fff", fontFamily: systemSansFont, fontSize: 14, lineHeight: 19, fontWeight: "900" },
  detail: { marginTop: 4, color: "rgba(255,255,255,0.52)", fontFamily: systemSansFont, fontSize: 12, lineHeight: 17, fontWeight: "500" },
  retry: { alignSelf: "flex-end", minHeight: 34, paddingHorizontal: 13, borderRadius: 5, flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 6, backgroundColor: "#fff" },
  retryActive: { backgroundColor: "#dedede", transform: [{ scale: 1.025 }] },
  retryPressed: { opacity: 0.78 },
  retryText: { color: "#111", fontFamily: systemSansFont, fontSize: 12, lineHeight: 16, fontWeight: "900" },
});
